import { createContext, useMemo, useReducer } from "react";
import type { Dispatch, PropsWithChildren } from "react";
import { useSafeContext } from "@aviv-unified-frontend/internal";

export type OverlayType =
  | "consent"
  | "custom-banner"
  | "adjust-banner"
  | "google-one-tap"
  | "airship"
  | "qualtrics"
  | "app-overlay";

/**
 * Represents the status of an overlay.
 *
 */
type OverlayStatus =
  /**
   * The overlay is in a pending state, waiting to be initialized.
   */
  | "pending"
  /**
   * The overlay has been initialized and is ready to be displayed.
   */
  | "initialized"
  /**
   * The overlay has been displayed and is currently active.
   */
  | "completed"
  /**
   * The overlay has been disabled and is no longer active.
   * This status is set only when the overlay is the next one to be displayed.
   */
  | "disabled";

export type OverlayState = {
  [key in OverlayType]: OverlayStatus;
};

type OverlayAction = {
  type: "INIT" | "COMPLETE" | "DISABLE";
  payload: OverlayType;
};

type OverlayOrchestratorContextValue = {
  state: OverlayState;
  dispatch: Dispatch<OverlayAction>;
};

const OverlayOrchestratorContext =
  createContext<OverlayOrchestratorContextValue | null>(null);

const statusByType = {
  INIT: "initialized",
  DISABLE: "disabled",
  COMPLETE: "completed",
} as const;

export const reducer = (state: OverlayState, action: OverlayAction) => {
  const status = statusByType[action.type];

  if (status) {
    return {
      ...state,
      [action.payload]: status,
    };
  }

  return state;
};

const initialState: OverlayState = {
  consent: "initialized",
  "custom-banner": "pending",
  "adjust-banner": "pending",
  "google-one-tap": "pending",
  airship: "pending",
  qualtrics: "pending",
  "app-overlay": "pending",
};

export const OverlayOrchestratorProvider = ({
  children,
}: PropsWithChildren) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const value = useMemo(() => ({ state, dispatch }), [state, dispatch]);

  return (
    <OverlayOrchestratorContext.Provider value={value}>
      {children}
    </OverlayOrchestratorContext.Provider>
  );
};

export const useOverlayOrchestrator = () =>
  useSafeContext(
    {
      package: "@aviv-unified-frontend/branded-shell",
      module: "overlayOrchestrator",
    },
    OverlayOrchestratorContext,
  );
